import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DataIngestionService } from "../../data-ingestion/data.ingestion.service";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { MultiselectModelI } from "../../s25-multiselect/s25.multiselect.component";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { UserStore } from "../../../stores/user.store";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import {
    PermissionDiagnosticCatalogEntry,
    PermissionDiagnosticService,
    PermissionExplainResponse,
} from "./permission.diagnostic.service";
import { IngestionPointCategory } from "../../data-ingestion/data.ingestion.const";

/** Connect25 actions. The permission registry also lists other products. */
const CONNECT25_DIAGNOSTIC_ACTION_NAMES = new Set([
    "BUDGET_VIEW",
    "BUDGET_MANAGE",
    "ORG_EDIT",
    "ORG_CONTACTS_VIEW",
    "ORG_CONTACTS_MANAGE",
    "ORG_NEWS_MANAGE",
    "ORG_FILES_VIEW",
    "ORG_FILES_MANAGE",
    "ORG_SECURITY_VIEW",
    "ORG_SECURITY_MANAGE",
    "EVENT_REQUEST",
    "EVENT_REGISTRATION_VIEW",
    "EVENT_REGISTRATION_MANAGE",
    "EVENT_ATTENDEES_VIEW",
    "EVENT_ATTENDEES_MANAGE",
    "EVENT_SURVEY_VIEW",
    "EVENT_SURVEY_MANAGE",
    "USER_SEARCH_VIEW",
    "USER_SEARCH_BULK_EDIT",
    "FORMS_VIEW",
    "FORMS_MANAGE",
    "ELECTION_VIEW",
    "ELECTION_MANAGE",
    "EMAIL_TEMPLATE_INSTITUTION_ADMIN",
    "ORG_EMAIL_VIEW",
    "ORG_EMAIL_MANAGE_TEMPLATES",
    "SERVICE_HOURS_LOG",
    "ORG_PAGE_VIEW",
    "CURRICULUM_VIEW",
    "CURRICULUM_MANAGE",
]);

/**
 * Admin-only diagnostic: explains permission checks for a named action (self or another contact).
 */
@TypeManagerDecorator("s25-ng-se-permission-diagnostic")
@Component({
    selector: "s25-ng-se-permission-diagnostic",
    host: { class: "se-perm-diagnostic-host" },
    template: `
        @if (diagnosticBootstrap.resource.isLoading()) {
            <div class="c-padding--double">
                <s25-loading-inline [model]="{ text: 'Loading diagnostic…' }" />
            </div>
        } @else if (diagnosticBootstrap.resource.error()) {
            <div class="c-padding--double se-security-config__error" role="alert">
                Permission diagnostics was unable to load.
            </div>
        } @else if (diagnosticBootstrap.value()?.isSysAdmin) {
            <div class="c-padding--double se-perm-diagnostic card">
                <h2 class="se-perm-diagnostic__title">Permission diagnostic</h2>
                <p class="ngFinePrint">
                    System administrators only. Choose an action, then run for yourself or for another contact (server
                    enforces admin for cross-user).
                </p>

                <div class="se-perm-diagnostic__row">
                    <label class="se-perm-diagnostic__label">
                        <span>Action</span>
                        <select
                            class="cn-form__control"
                            [ngModel]="selectedAction()"
                            (ngModelChange)="onActionChange($event)"
                        >
                            <option value="">Select an action…</option>
                            @for (a of actionRows(); track a.name) {
                                <option [value]="a.name">{{ a.label }}</option>
                            }
                        </select>
                    </label>
                </div>

                @if (selectedActionIsIngestionPoint()) {
                    <div class="se-perm-diagnostic__row">
                        <label class="se-perm-diagnostic__label">
                            <span>Form</span>
                            @if (engagementForms.resource.isLoading()) {
                                <s25-loading-inline [model]="{ text: 'Loading forms…' }" />
                            }
                            <select
                                class="cn-form__control"
                                [ngModel]="selectedIngestionPointId()"
                                (ngModelChange)="onFormIngestionPointIdChange($event)"
                            >
                                <option [ngValue]="null">Select a form…</option>
                                @for (form of engagementForms.value(); track form.ingestionPointId) {
                                    <option [ngValue]="form.ingestionPointId">
                                        {{ form.ingestionPointName || "Form " + form.ingestionPointId }}
                                    </option>
                                }
                            </select>
                        </label>
                    </div>
                }

                <div class="se-perm-diagnostic__row se-perm-diagnostic__contact-row">
                    <label class="se-perm-diagnostic__label">
                        <span>Contact (optional)</span>
                        @if (contactPickerBean) {
                            <s25-ng-multiselect-search-criteria
                                [type]="'contacts'"
                                [buttonText]="'Select contact'"
                                [modelBean]="contactPickerBean"
                                [selectedItems]="contactPickerBean.selectedItems ?? []"
                                [showResults]="true"
                                [popoverOnBody]="true"
                                [useSecurity]="false"
                                (close)="onContactPickerClose($event)"
                            />
                        }
                        <button type="button" class="aw-button se-perm-diagnostic__clear" (click)="clearContact()">
                            Clear contact
                        </button>
                    </label>
                    <p class="ngFinePrint se-perm-diagnostic__hint">
                        Leave contact empty to evaluate the <strong>signed-in user</strong> for this organization
                        context. Select a contact to evaluate that user (admin only).
                    </p>
                </div>

                @if (orgHint()) {
                    <p class="ngFinePrint">{{ orgHint() }}</p>
                }

                <div class="se-perm-diagnostic__actions">
                    <button
                        type="button"
                        class="aw-button aw-button--primary"
                        [disabled]="runDisabled()"
                        (click)="runDiagnostic()"
                    >
                        Run diagnostic
                    </button>
                </div>

                @if (runError()) {
                    <div class="se-security-config__error se-perm-diagnostic__error" role="alert">
                        {{ runError() }}
                    </div>
                }

                @if (running()) {
                    <s25-loading-inline [model]="{ text: 'Running…' }" />
                }

                @if (result(); as res) {
                    <div class="se-perm-diagnostic__result card" role="region" aria-label="Diagnostic result">
                        <h3 class="se-perm-diagnostic__subhead">Result</h3>
                        <p>
                            <strong>Action:</strong> {{ res.action }}
                            @if (selectedActionIsIngestionPoint() && selectedIngestionPointId() != null) {
                                <span> · <strong>Form ID:</strong> {{ selectedIngestionPointId() }}</span>
                            }
                            @if (res.orgId != null) {
                                <span> · <strong>Organization:</strong> {{ res.orgId }}</span>
                            }
                            @if (lastExplainedIngestionPointId() != null) {
                                <span> · <strong>Form id:</strong> {{ lastExplainedIngestionPointId() }}</span>
                            }
                            · <strong>Contact evaluated:</strong> {{ res.contactId }}
                        </p>
                        <p>
                            <strong>Allowed:</strong>
                            {{ res.allowed ? "Yes" : "No" }}
                        </p>
                        @if (res.denyReason) {
                            <p><strong>Reason:</strong> {{ res.denyReason }}</p>
                        }
                        @if (res.statusMessages?.length) {
                            <div class="se-perm-diagnostic__status-messages" role="status">
                                <strong>Status messages:</strong>
                                <ul>
                                    @for (message of res.statusMessages; track message) {
                                        <li [innerHTML]="message | linkifyStatusMessage"></li>
                                    }
                                </ul>
                            </div>
                        }
                        @if (res.trace?.length) {
                            <table class="se-perm-diagnostic__trace-table">
                                <caption class="sr-only">
                                    Permission layers
                                </caption>
                                <thead>
                                    <tr>
                                        <th scope="col">Layer</th>
                                        <th scope="col">Granted</th>
                                        <th scope="col">Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of res.trace; track $index) {
                                        <tr>
                                            <td>{{ row.layer }}</td>
                                            <td>{{ row.granted ? "Yes" : "No" }}</td>
                                            <td>{{ row.detail }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        }
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .se-perm-diagnostic {
            margin-top: 1.5rem;
            background: #fafafa;
            border: 1px solid #ddd;
            border-radius: 8px;
        }

        .se-perm-diagnostic__title {
            margin-top: 0;
            font-size: 1.15rem;
        }

        .se-perm-diagnostic__subhead {
            font-size: 1rem;
            margin: 0 0 0.5rem;
        }

        .se-perm-diagnostic__label {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            font-weight: 600;
            max-width: 36rem;
        }

        .se-perm-diagnostic__label select {
            max-width: 100%;
        }

        .se-perm-diagnostic__label input[type="number"] {
            max-width: 12rem;
        }

        .se-perm-diagnostic__row {
            margin-bottom: 1rem;
        }

        .se-perm-diagnostic__contact-row s25-ng-multiselect-search-criteria {
            max-width: 28rem;
        }

        .se-perm-diagnostic__clear {
            align-self: flex-start;
            margin-top: 0.35rem;
        }

        .se-perm-diagnostic__requirements {
            margin: 1rem 0;
            padding: 0.75rem 1rem;
            background: #fff;
            border-radius: 6px;
            border: 1px solid #e0e0e0;
        }

        .se-perm-diagnostic__req-text {
            margin: 0;
            line-height: 1.45;
        }

        .se-perm-diagnostic__hint {
            margin: 0.5rem 0 0;
            max-width: 42rem;
        }

        .se-perm-diagnostic__actions {
            margin: 1rem 0;
        }

        .se-perm-diagnostic__error {
            margin: 0.75rem 0;
        }

        .se-perm-diagnostic__result {
            margin-top: 1rem;
            background: #fff;
        }

        .se-perm-diagnostic__trace-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 0.75rem;
            font-size: 0.95rem;
        }

        .se-perm-diagnostic__trace-table th,
        .se-perm-diagnostic__trace-table td {
            border: 1px solid #ddd;
            padding: 0.4rem 0.6rem;
            text-align: left;
            vertical-align: top;
        }

        .se-perm-diagnostic__trace-table th {
            background: #eee;
        }

        .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            border: 0;
        }

        .se-security-config__error {
            padding: 0.75rem 1rem;
            margin-bottom: 1rem;
            border-radius: 6px;
            background: #fdecea;
            color: #611a15;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25LoadingInlineModule, S25MultiselectModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SePermissionDiagnosticComponent implements OnInit {
    /** Organization context for org-scoped checks; omit on global security screen. */
    readonly orgId = input<number | undefined>(undefined);

    private readonly userStore = inject(UserStore);

    protected readonly diagnosticBootstrap = s25Resource({
        loader: async (): Promise<{
            isSysAdmin: boolean;
            actions: PermissionDiagnosticCatalogEntry[];
            ingestionPointActionNames: ReadonlySet<string>;
        }> => {
            if (this.userStore.secGroupId() !== -1) {
                return { isSysAdmin: false, actions: [], ingestionPointActionNames: new Set() };
            }
            const catalog = await PermissionDiagnosticService.listActions();
            const generalActions = (catalog.actions ?? []).filter((entry) =>
                CONNECT25_DIAGNOSTIC_ACTION_NAMES.has(entry.name),
            );
            const formActions = catalog.ingestionPointActions ?? [];
            const ingestionPointActionNames = new Set(formActions.map((entry) => entry.name));
            return {
                isSysAdmin: true,
                actions: [...generalActions, ...formActions],
                ingestionPointActionNames,
            };
        },
    });

    protected readonly selectedAction = signal("");

    protected readonly selectedIngestionPointId = signal<number | null>(null);

    protected readonly selectedActionIsIngestionPoint = computed((): boolean => {
        const name = this.selectedAction();
        if (!name) return false;
        return (this.diagnosticBootstrap.value()?.ingestionPointActionNames ?? new Set()).has(name);
    });

    /** Engagement forms for the form-action picker (same access layer as {@code IngestionPointAccessController}). */
    protected readonly engagementForms = s25Resource({
        if: () => this.selectedActionIsIngestionPoint(),
        params: () => this.selectedAction(),
        loader: () => DataIngestionService.getIngestionPoints(IngestionPointCategory.ENGAGEMENT),
    });

    protected readonly actionRows = computed((): PermissionDiagnosticCatalogEntry[] => {
        return this.diagnosticBootstrap.value()?.actions ?? [];
    });

    protected readonly selectedActionRequiresIngestionPointId = computed((): boolean => {
        const actionName = this.selectedAction();
        if (!actionName) {
            return false;
        }
        return this.diagnosticBootstrap.value()?.ingestionPointActionNames.has(actionName) ?? false;
    });

    protected readonly diagnosticFormIngestionPointId = signal<number | null>(null);

    /** Shown on the last successful explain when a form id was sent. */
    protected readonly lastExplainedIngestionPointId = signal<number | null>(null);

    protected readonly actionMeta = computed((): PermissionDiagnosticCatalogEntry | undefined => {
        const name = this.selectedAction();
        if (!name) return undefined;
        return this.actionRows().find((a) => a.name === name);
    });

    protected readonly targetContactId = signal<number | null>(null);

    protected readonly result = signal<PermissionExplainResponse | null>(null);
    protected readonly runError = signal<string | null>(null);
    protected readonly running = signal(false);

    protected contactPickerBean: MultiselectModelI | null = null;

    protected readonly orgHint = computed(() => {
        if (this.orgId() != null) return null;
        return "No organization is selected — org-scoped rights may not apply until you open this from an organization.";
    });

    ngOnInit(): void {
        this.contactPickerBean = {
            uuid: `se-perm-diag-contact-${this.orgId() ?? "global"}`,
            singleSelect: true,
            title: "Contacts",
            selectedItems: [],
        };
    }

    protected onActionChange(value: string): void {
        this.selectedAction.set(value);
        this.diagnosticFormIngestionPointId.set(null);
        this.lastExplainedIngestionPointId.set(null);
        this.result.set(null);
        this.runError.set(null);
    }

    protected onFormIngestionPointIdChange(raw: unknown): void {
        if (raw === "" || raw === null || raw === undefined) {
            this.diagnosticFormIngestionPointId.set(null);
            return;
        }
        const numeric = typeof raw === "number" && Number.isFinite(raw) ? raw : Number.parseInt(String(raw).trim(), 10);
        this.diagnosticFormIngestionPointId.set(Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : null);
        this.lastExplainedIngestionPointId.set(null);
        this.result.set(null);
        this.runError.set(null);
    }

    protected onContactPickerClose(selected: S25ItemI[] | undefined): void {
        const items = selected ?? [];
        const first = items[0];
        const contactItemId = first?.itemId != null ? Number(first.itemId) : null;
        this.targetContactId.set(Number.isFinite(contactItemId) && contactItemId! > 0 ? contactItemId : null);
        if (this.contactPickerBean) {
            this.contactPickerBean.selectedItems = [...items];
        }
        this.lastExplainedIngestionPointId.set(null);
        this.result.set(null);
        this.runError.set(null);
    }

    protected clearContact(): void {
        this.targetContactId.set(null);
        this.lastExplainedIngestionPointId.set(null);
        if (this.contactPickerBean) {
            this.contactPickerBean.selectedItems = [];
        }
        this.result.set(null);
        this.runError.set(null);
    }

    protected runDisabled(): boolean {
        if (!this.selectedAction() || this.running()) {
            return true;
        }
        if (
            this.selectedActionRequiresIngestionPointId() &&
            (this.diagnosticFormIngestionPointId() == null || this.diagnosticFormIngestionPointId()! <= 0)
        ) {
            return true;
        }
        return false;
    }

    protected async runDiagnostic(): Promise<void> {
        const action = this.selectedAction();
        if (!action) return;

        this.running.set(true);
        this.runError.set(null);
        this.result.set(null);
        this.lastExplainedIngestionPointId.set(null);

        try {
            const orgId = this.orgId();
            const targetContactIdForRequest = this.targetContactId();
            const formIngestionPointId = this.selectedActionRequiresIngestionPointId()
                ? (this.diagnosticFormIngestionPointId() ?? undefined)
                : undefined;
            const res = await PermissionDiagnosticService.explain(
                action,
                orgId,
                targetContactIdForRequest ?? undefined,
                formIngestionPointId,
            );
            this.result.set(res);
            if (formIngestionPointId != null) {
                this.lastExplainedIngestionPointId.set(formIngestionPointId);
            }
        } catch (err: unknown) {
            const defaultMsg = "Request failed. Check the action name and your permissions.";
            this.runError.set(S25Util.errorText(err, defaultMsg) ?? defaultMsg);
        } finally {
            this.running.set(false);
        }
    }
}
